/**
 * Returns true if the given input looks like a localhost URL/host.
 *
 * Accepts free-form inputs (with or without protocol). Detects these patterns:
 *  - Hostnames equal to or ending with `.localhost` (e.g., localhost, app.localhost)
 *  - Loopback addresses in IPv4 127.0.0.0/8 (e.g., 127.0.0.1)
 *  - Common local bind addresses: 0.0.0.0
 *  - IPv6 loopback ::1
 */
export function isLocalhostLike(input: string): boolean {
  const val = (input || '').trim();
  if (!val) {
    return false;
  }

  // Ensure there is a protocol for URL parsing (case insensitive)
  const lowerVal = val.toLowerCase();
  const withProtocol = normalizeWebsiteUrl(lowerVal);
  try {
    const url = new URL(withProtocol);
    const host = (url.hostname || '').toLowerCase();

    // localhost and subdomains like app.localhost
    if (host === 'localhost' || host.endsWith('.localhost')) {
      return true;
    }

    // IPv4 loopback range 127.0.0.0/8
    if (/^127(?:\.\d{1,3}){3}$/.test(host)) {
      return true;
    }

    // Common local bind address
    if (host === '0.0.0.0') {
      return true;
    }

    // IPv6 loopback (with or without brackets)
    if (host === '::1' || host === '[::1]') {
      return true;
    }

    return false;
  } catch {
    // If URL parsing fails, assume not localhost-like
    return false;
  }
}

export function normalizeWebsiteUrl(url: string): string {
  const trimmed = url.trim();
  return trimmed.startsWith('http://') || trimmed.startsWith('https://') ? trimmed : `https://${trimmed}`;
}

/**
 * Documentation URL for creating a CLI tunnel to support localhost testing.
 */
export const LOCALHOST_TUNNEL_DOC =
  'https://docs.stably.ai/platform-capabilities/localhost-support#creating-the-tunnel' as const;
